import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Menu, X, Bell, ChevronDown, User, LogOut, LayoutDashboard, ArrowRight, Star } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/hooks/useAuth'
import { getInitials } from '@/lib/utils'
import { cn } from '@/lib/utils'
import { BrandLogo } from '@/components/shared/BrandLogo'

const navLinks = [
  { label: 'Como funciona', href: '/como-funciona' },
  { label: 'Instrutores', href: '/instrutores' },
  { label: 'Seja instrutor', href: '/seja-instrutor' },
]

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const { user, profile, signOut } = useAuth()
  // "Como funciona" e "Seja instrutor" são para quem ainda não tem conta.
  const visibleLinks = user ? navLinks.filter((l) => l.href === '/instrutores') : navLinks
  const navigate = useNavigate()
  const location = useLocation()

  const isLanding = location.pathname === '/'
  const transparent = isLanding && !scrolled && !mobileOpen

  const isActive = (href: string) =>
    location.pathname === href || (href !== '/' && location.pathname.startsWith(href))

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => { setMobileOpen(false) }, [location.pathname])

  const dashboardUrl =
    profile?.role === 'instructor' ? '/instrutor/dashboard' :
    profile?.role === 'admin' ? '/admin' :
    '/aluno/dashboard'

  // O sino levava todo mundo para /aluno/dashboard — um instrutor logado batia
  // no RoleRoute e era rebatido para o próprio painel.
  const notificationsUrl =
    profile?.role === 'instructor' ? '/instrutor/notificacoes/inbox' :
    profile?.role === 'admin' ? '/admin/notificacoes' :
    '/aluno/notificacoes/inbox'

  return (
    <header className={cn(
      'sticky top-0 z-50 w-full transition-all duration-300',
      transparent
        ? 'bg-transparent'
        : 'bg-white/96 dark:bg-gray-900/96 backdrop-blur-2xl border-b border-slate-200/80 dark:border-gray-800',
    )}>

      {/* Accent line — only when solid */}
      {!transparent && (
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-300/60 to-transparent pointer-events-none" />
      )}

      <div className="mx-auto max-w-6xl flex h-[112px] items-center justify-between px-4 sm:px-6">

        {/* ── Logo ─────────────────────────────────────────────────── */}
        <Link to="/" className="flex items-center shrink-0">
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 420, damping: 20 }}
          >
            <BrandLogo onDark={transparent} to={null} className="h-12" />
          </motion.div>
        </Link>

        {/* ── Desktop nav ───────────────────────────────────────────── */}
        <nav className="hidden md:flex items-center gap-0.5">
          {visibleLinks.map((link) => {
            const active = isActive(link.href)
            return (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  'relative px-4 py-2 text-[13.5px] font-medium rounded-lg transition-colors duration-150',
                  transparent
                    ? active
                      ? 'text-white'
                      : 'text-white/65 hover:text-white hover:bg-white/8'
                    : active
                      ? 'text-brand-700 dark:text-brand-400'
                      : 'text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100/70 dark:hover:bg-gray-800',
                )}
              >
                {link.label}
                {active && (
                  <motion.span
                    layoutId="nav-indicator"
                    className={cn(
                      'absolute bottom-[3px] left-3 right-3 h-[2px] rounded-full',
                      transparent ? 'bg-white/70' : 'bg-brand-500',
                    )}
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  />
                )}
              </Link>
            )
          })}
        </nav>

        {/* ── Desktop actions ───────────────────────────────────────── */}
        <div className="hidden md:flex items-center gap-2">
          {/* Dark mode toggle */}
          {user ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                className={cn('h-9 w-9 rounded-lg', transparent ? 'text-white/70 hover:text-white hover:bg-white/10' : 'text-slate-500')}
                onClick={() => navigate(notificationsUrl)}
                aria-label="Notificações"
              >
                <Bell className="h-[17px] w-[17px]" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className={cn(
                    'flex items-center gap-2 rounded-xl px-2.5 py-1.5 text-sm font-medium transition-colors',
                    transparent
                      ? 'text-white/90 hover:bg-white/10'
                      : 'text-slate-700 dark:text-gray-200 hover:bg-slate-100 dark:hover:bg-gray-800 border border-slate-200/70 dark:border-gray-700',
                  )}>
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={profile?.avatar_url ?? undefined} />
                      <AvatarFallback className="bg-brand-600 text-white text-[10px] font-bold">
                        {getInitials(profile?.full_name ?? user.email ?? 'U')}
                      </AvatarFallback>
                    </Avatar>
                    <span className="max-w-[90px] truncate text-[13px]">
                      {profile?.full_name?.split(' ')[0] ?? 'Usuário'}
                    </span>
                    <ChevronDown className="h-3.5 w-3.5 opacity-40 shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 shadow-card rounded-xl border-slate-200/80">
                  <DropdownMenuItem className="rounded-lg" onClick={() => navigate(dashboardUrl)}>
                    <LayoutDashboard className="mr-2 h-4 w-4 text-brand-500" />
                    Painel
                  </DropdownMenuItem>
                  <DropdownMenuItem className="rounded-lg" onClick={() => navigate(
                    profile?.role === 'instructor' ? '/instrutor/perfil' : '/aluno/perfil'
                  )}>
                    <User className="mr-2 h-4 w-4 text-slate-400" />
                    Meu perfil
                  </DropdownMenuItem>
                  {profile?.role === 'student' && (
                    <DropdownMenuItem className="rounded-lg" onClick={() => navigate('/aluno/avaliacoes')}>
                      <Star className="mr-2 h-4 w-4 text-amber-400" />
                      Minhas avaliações
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="rounded-lg text-rose-600 focus:text-rose-600 focus:bg-rose-50" onClick={signOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                asChild
                className={cn(
                  'h-9 px-4 text-[13.5px] font-medium rounded-lg',
                  transparent ? 'text-white/80 hover:text-white hover:bg-white/10' : 'text-slate-600',
                )}
              >
                <Link to="/login">Entrar</Link>
              </Button>

              <Button
                asChild
                className={cn(
                  'h-9 px-4 text-[13.5px] font-semibold rounded-lg gap-1.5',
                  transparent
                    ? 'bg-white text-brand-700 hover:bg-white/90 shadow-sm'
                    : 'bg-brand-600 text-white hover:bg-brand-700 shadow-brand/40',
                )}
              >
                <Link to="/cadastro">
                  Criar conta
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </Button>
            </>
          )}
        </div>

        {/* ── Mobile toggle ─────────────────────────────────────────── */}
        <motion.button
          whileTap={{ scale: 0.88 }}
          className={cn(
            'md:hidden p-2.5 rounded-xl',
            transparent ? 'text-white hover:bg-white/10' : 'text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-800',
          )}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Menu"
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={mobileOpen ? 'x' : 'menu'}
              initial={{ rotate: -70, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 70, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </motion.div>
          </AnimatePresence>
        </motion.button>
      </div>

      {/* ── Mobile menu ───────────────────────────────────────────── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden border-t border-slate-200/60 dark:border-gray-800 bg-white/98 dark:bg-gray-900/98 backdrop-blur-xl shadow-lg"
          >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 py-3 flex flex-col gap-0.5">
              {visibleLinks.map((link, i) => {
                const active = isActive(link.href)
                return (
                  <motion.div
                    key={link.href}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.04 + 0.04 }}
                  >
                    <Link
                      to={link.href}
                      className={cn(
                        'flex items-center gap-2 px-3.5 py-3 rounded-xl text-sm font-medium transition-colors',
                        active
                          ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300'
                          : 'text-slate-700 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800',
                      )}
                      onClick={() => setMobileOpen(false)}
                    >
                      {active && <span className="h-1.5 w-1.5 rounded-full bg-brand-500 shrink-0" />}
                      {link.label}
                    </Link>
                  </motion.div>
                )
              })}

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.16 }}
                className="pt-3 mt-1 border-t border-slate-200/60 dark:border-gray-800 flex flex-col gap-2"
              >
                {user ? (
                  <>
                    <Button variant="outline" className="rounded-xl" asChild onClick={() => setMobileOpen(false)}>
                      <Link to={dashboardUrl}>Meu painel</Link>
                    </Button>
                    <Button
                      variant="ghost"
                      className="rounded-xl text-rose-500 hover:text-rose-600 hover:bg-rose-50"
                      onClick={() => { signOut(); setMobileOpen(false) }}
                    >
                      Sair
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" className="rounded-xl" asChild onClick={() => setMobileOpen(false)}>
                      <Link to="/login">Entrar</Link>
                    </Button>
                    <Button className="rounded-xl gap-1.5" asChild onClick={() => setMobileOpen(false)}>
                      <Link to="/cadastro">
                        Criar conta
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </Button>
                  </>
                )}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
